import {
  faChevronDown,
  faUser,
  faUserCircle,
  faCheck,
} from "@fortawesome/pro-regular-svg-icons";
import { isObject } from "lodash-es";
import { twJoin } from "tailwind-merge";

import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { SearchableSelect } from "src/base-components/SearchableSelect";
import { SkeletonPlaceholder } from "src/base-components/SkeletonPlaceholder";
import { CaseAssigneePill } from "src/cases/common/CaseAssigneePill";
import { OrganizationUser } from "src/clients/taktile-api";
import { Avatar } from "src/design-system/Avatar";
import { EmptyState } from "src/design-system/EmptyState";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";
import { canUserBeAssignee, getUserName } from "src/utils/user";

type UserOption = {
  key: string;
  value: OrganizationUser;
};

const UNSET_OPTION = { key: "unset", value: "Unset user" };

const isUser = (user?: unknown): user is OrganizationUser =>
  isObject(user) && "role" in user;

type CaseAssigneeSelectProps = {
  value?: Nullable<string>;
  users: OrganizationUser[];
  onChange: (value: Nullable<string>) => void;
  disabled?: boolean;
};

export const CaseAssigneeSelect: React.FC<CaseAssigneeSelectProps> = ({
  value,
  users,
  onChange,
  disabled = false,
}) => {
  const { wsId } = useParamsDecode<DashboardPageParams>();

  const options: (UserOption | typeof UNSET_OPTION)[] =
    users
      ?.filter((user) => canUserBeAssignee(wsId, user))
      .map((user) => ({ key: user.id, value: user })) ?? [];

  const userValue = users.find((user) => user.id === value);

  return (
    <SearchableSelect<OrganizationUser | string>
      buttonAs="div"
      buttonRenderer={() => (
        <div
          className={twJoin(
            "flex max-w-42 items-center truncate",
            !disabled && "cursor-pointer",
          )}
        >
          {userValue ? (
            <CaseAssigneePill assignee={userValue} withChevron={!disabled} />
          ) : (
            <Button
              disabled={disabled}
              iconLeft={faUser}
              iconRight={faChevronDown}
              size="sm"
              variant="secondary"
            >
              Select assignee
            </Button>
          )}
        </div>
      )}
      dataLoc="case-assignee-select"
      disabled={disabled}
      emptyState={
        <EmptyState
          description="Change your search query to try again"
          headline="No users found"
          icon={faUser}
        />
      }
      isLoading={users.length === 0}
      itemRenderer={({ value: item }) =>
        isUser(item) ? (
          <div className="flex w-full items-center gap-x-2 pr-2 text-gray-800 font-inter-normal-13px">
            <Avatar user={item} />
            <p className="truncate">{getUserName(item)}</p>
          </div>
        ) : (
          <div className="flex w-full items-center gap-x-2 text-gray-800 font-inter-normal-13px">
            <div className="px-px">
              <Icon
                color="text-gray-400"
                icon={faUserCircle}
                padding={false}
                size="sm"
              />
            </div>
            No assignee
            {!value && (
              <div className="ml-auto">
                <Icon color="text-indigo-600" icon={faCheck} size="sm" />
              </div>
            )}
          </div>
        )
      }
      options={[UNSET_OPTION, ...options]}
      placeholder="Select Assignee"
      searchOptions={{
        keys: ["full_name", "username", "email"],
        threshold: 0.3,
      }}
      searchPlaceholder="Search user..."
      selected={value}
      skeletonRenderer={() => (
        <div className="flex items-center gap-x-2 px-4 py-2 font-inter-normal-13px">
          <SkeletonPlaceholder
            height="h-4.5"
            rounded="rounded-full"
            width="w-4.5"
          />
          <div className="flex-auto">
            <SkeletonPlaceholder height="h-4" width="w-full" />
          </div>
        </div>
      )}
      onChange={(userId) => {
        if (userId === UNSET_OPTION["key"] || userId === value) {
          return onChange(null);
        }

        return onChange(userId);
      }}
    />
  );
};
